import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { initializeAuth, getCurrentUser } from '../services/authService';
import { getUserPermissions } from '../features/access-management/services/accessManagementService';
import { setPermissions, setPermissionsLoading } from '../app/store/slices/authSlice';
import { selectPermissions } from '../app/store/slices/authSlice';

// ✅ Define public routes that should NOT trigger store info API calls
const PUBLIC_ROUTES = [
  '/',
  '/home',
  '/login',
  '/register',
  '/forgot-password',
  '/privacy-policy',
  '/terms-of-service',
];

/**
 * Component to handle auth initialization based on current route
 * - Loads permissions immediately on mount if user exists (needed for route protection)
 * - Only loads store info when NOT on public pages (performance optimization)
 */
const AuthInitializer = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const permissions = useSelector(selectPermissions);
  const permissionsLoadedRef = useRef(false);

  // ✅ Load permissions immediately on mount (critical for route protection)
  useEffect(() => {
    const loadPermissions = async () => {
      const user = getCurrentUser();
      
      // Only load if user exists and permissions aren't already loaded
      if (user && !permissionsLoadedRef.current && Object.keys(permissions || {}).length === 0) {
        permissionsLoadedRef.current = true;
        
        // ✅ Set loading state IMMEDIATELY (synchronously) before async call
        dispatch(setPermissionsLoading(true));
        
        try {
          const response = await getUserPermissions(user.id || user.email);
          const perms = response?.payload || response?.permissions || {};
          dispatch(setPermissions(perms));
        } catch (error) {
          console.error('❌ Error loading user permissions:', error);
          dispatch(setPermissions({}));
        } finally {
          dispatch(setPermissionsLoading(false));
        }
      }
    };

    loadPermissions();
  }, [dispatch, permissions]); // Run when dispatch or permissions change

  // ✅ Load store info based on current route (skip for public routes)
  useEffect(() => {
    const loadStoreInfo = async () => {
      const currentPath = location.pathname;
      const isPublicRoute = PUBLIC_ROUTES.includes(currentPath);
      const user = getCurrentUser();
      
      // ✅ Only load store info (API call) when on protected routes
      if (!isPublicRoute && user) {
        await initializeAuth();
      }
    };

    loadStoreInfo();
  }, [location.pathname]);

  return null; // This component doesn't render anything
};

export default AuthInitializer;
